import { z } from 'zod';
import { IdSchema, TimestampSchema } from './common.js';
import {
  DestinationSchema, InboundRouteSaveSchema, IvrMenuUpsertSchema, QueueUpsertSchema, RingGroupUpsertSchema,
  ScheduleRuleSchema, ScheduleUpsertSchema,
  type InboundRoute, type IvrMenu, type Queue, type RingGroup,
} from './tms.js';

/**
 * Call flows (docs/41): Classic and Flows, two ways of editing the one set of routing.
 *
 * The mode is the account's own choice, made in the client portal. It changes which editor opens,
 * never what a call does: both edit the same pieces, and the switch runs those.
 */
export const RoutingModeSchema = z.enum(['classic', 'flows']);
export type RoutingMode = z.infer<typeof RoutingModeSchema>;

export const RoutingModeSaveSchema = z.object({ mode: RoutingModeSchema }).strict();

// ------------------------------------------------------------------ message
/** A recording, then on to the next step (docs/41 §3). */
export const FlowMessageSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  media_id: IdSchema.nullable(),
  media_name: z.string().nullable(),
  next_destination: DestinationSchema.nullable(),
  created_at: TimestampSchema,
});
export type FlowMessage = z.infer<typeof FlowMessageSchema>;

export const FlowMessageUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  media_id: IdSchema,
  /** Where the caller goes once it has played. None ends the call. */
  next_destination: DestinationSchema.nullable().optional(),
}).strict();
export type FlowMessageUpsert = z.infer<typeof FlowMessageUpsertSchema>;

// -------------------------------------------------------------- hours check
/** Open or closed, anywhere in a flow (docs/41 §3): a shared schedule, or hours of its own. */
export const HoursCheckHoursSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('schedule'), schedule_id: IdSchema }),
  z.object({
    mode: z.literal('own'),
    timezone: z.string().max(64).nullable().optional(),
    rules: ScheduleUpsertSchema.shape.rules,
  }),
]);

export const HoursCheckSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  hours: z.object({
    mode: z.enum(['schedule', 'own']),
    schedule_id: IdSchema.nullable(),
    /** The shared schedule's name; null for hours of its own. */
    schedule_name: z.string().nullable(),
    timezone: z.string(),
    /** Its own hours; empty for a shared schedule. */
    rules: z.array(ScheduleRuleSchema),
    /** From the evaluation the switch makes at call time. */
    open_now: z.boolean().nullable(),
    explanation: z.string().nullable(),
  }),
  open_destination: DestinationSchema.nullable(),
  closed_destination: DestinationSchema.nullable(),
  created_at: TimestampSchema,
});
export type HoursCheck = z.infer<typeof HoursCheckSchema>;

export const HoursCheckUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  hours: HoursCheckHoursSchema,
  /** None ends the call on that branch. */
  open_destination: DestinationSchema.nullable().optional(),
  closed_destination: DestinationSchema.nullable().optional(),
}).strict();
export type HoursCheckUpsert = z.infer<typeof HoursCheckUpsertSchema>;

// -------------------------------------------------------------------- drafts
/**
 * The pieces a flow is made of, as a draft names them. Each saves through the same schema, and the
 * same code, as its own page in Classic.
 */
export const FlowPieceKindSchema = z.enum(['inbound_route', 'ivr', 'ring_group', 'queue', 'message', 'hours']);
export type FlowPieceKind = z.infer<typeof FlowPieceKindSchema>;

export const FLOW_PIECE_SCHEMAS = {
  inbound_route: InboundRouteSaveSchema,
  ivr: IvrMenuUpsertSchema,
  ring_group: RingGroupUpsertSchema,
  queue: QueueUpsertSchema,
  message: FlowMessageUpsertSchema,
  hours: HoursCheckUpsertSchema,
} as const;

/**
 * One piece as the draft would have it. `body` is checked against the piece's own schema when the
 * draft is published, not before: a draft may hold a step nobody has finished yet. A new piece's id
 * is made by the portal, so steps in the draft can point at it before it exists.
 */
export const FlowChangeSchema = z.object({
  kind: FlowPieceKindSchema,
  id: IdSchema,
  op: z.enum(['create', 'update']),
  body: z.record(z.unknown()),
  /** When the draft first changed this piece; set by the server. A newer piece is a conflict. */
  base: z.string().nullable().optional(),
}).strict();
export type FlowChange = z.infer<typeof FlowChangeSchema>;

export const FlowDraftSaveSchema = z.object({
  /** The route the flow starts from; null for a flow whose route is new in the draft. */
  route_id: IdSchema.nullable(),
  changes: z.array(FlowChangeSchema).max(200),
}).strict();
export type FlowDraftSave = z.infer<typeof FlowDraftSaveSchema>;

export const FlowDraftSchema = z.object({
  id: IdSchema,
  route_id: IdSchema.nullable(),
  /** The route's name as the draft has it, else as it is live. */
  name: z.string(),
  changes: z.array(FlowChangeSchema),
  updated_at: TimestampSchema,
  updated_by: z.string().nullable(),
  /** A draft a restore opened as: the version it went back to (docs/41 §12). */
  restored_from: z.object({ id: IdSchema, at: TimestampSchema }).nullable().optional(),
});
export type FlowDraft = z.infer<typeof FlowDraftSchema>;

/** Why a draft could not be published, piece by piece. */
export const FlowProblemSchema = z.object({
  kind: FlowPieceKindSchema,
  id: IdSchema,
  name: z.string(),
  problem: z.enum(['invalid', 'changed', 'missing']),
  message: z.string(),
});
export type FlowProblem = z.infer<typeof FlowProblemSchema>;

export const FlowPublishResultSchema = z.object({
  route_id: IdSchema,
  /** Pieces written. */
  applied: z.number().int(),
  /** New pieces the flow no longer reaches, left out. */
  dropped: z.number().int(),
});
export type FlowPublishResult = z.infer<typeof FlowPublishResultSchema>;

// ------------------------------------------------------------------ AI: describe it in a sentence
/**
 * A flow described in words (docs/41 §7). What comes back is a proposal for the draft: where the
 * route's calls should go first, and the new steps that leads through. The editor puts it into the
 * draft; nothing reaches a call until somebody publishes.
 */
export const FlowAiRequestSchema = z.object({
  prompt: z.string().trim().min(3).max(1000),
  /** The flow's name, so the model can name its steps after it. */
  route_name: z.string().max(80).optional(),
}).strict();
export type FlowAiRequest = z.infer<typeof FlowAiRequestSchema>;

export const FlowAiResultSchema = z.object({
  /** Where the route's calls go first; null when nothing in the description could be matched. */
  start: DestinationSchema.nullable(),
  /** New steps only: everything already in the account is referred to by its id. */
  changes: z.array(FlowChangeSchema),
  /** What the model could not match or assumed, in words for the person who asked. */
  notes: z.array(z.string()),
  /**
   * Words for recordings nobody has made yet (0160): a message's or a menu greeting's, where no
   * recording the account has fits. The step offers to make one from them; nothing is made unasked.
   */
  suggestions: z.array(z.object({
    kind: z.enum(['message', 'ivr']), id: IdSchema, field: z.enum(['media_id', 'greeting_media_id']), text: z.string(),
  })).default([]),
  /** Whose key paid for it. */
  key: z.enum(['account', 'platform']),
});
export type FlowAiResult = z.infer<typeof FlowAiResultSchema>;

// ------------------------------------------------------------------ a piece's settings, as a save takes them
type PieceBody = Record<string, unknown>;
export type PieceRead = InboundRoute | IvrMenu | RingGroup | Queue | FlowMessage | HoursCheck;

/** A schedule rule as a save takes it: no ids, no nulls. */
function ruleBody(r: Record<string, unknown>): PieceBody {
  const out: PieceBody = {};
  for (const k of ['kind', 'days_of_week', 'time_from', 'time_to', 'date_from', 'date_to', 'is_open', 'priority', 'label']) {
    if (r[k] !== null && r[k] !== undefined) out[k] = r[k];
  }
  return out;
}

/**
 * A piece in its READ shape (what its list returns) as its SAVE shape (what its upsert takes). The
 * flow editor builds drafts with it, and the server snapshots live routing with it (docs/41 §12), so
 * both describe a piece the same way.
 */
export function saveShapeOf(kind: FlowPieceKind, r: PieceRead): PieceBody {
  switch (kind) {
    case 'inbound_route': {
      const x = r as InboundRoute;
      return {
        name: x.name, number_ids: x.numbers.map((n) => n.id), destination: x.destination,
        hours: x.hours.mode === 'always' ? { mode: 'always' }
          : x.hours.mode === 'schedule' ? { mode: 'schedule', schedule_id: x.hours.schedule_id }
            : { mode: 'own', timezone: x.hours.timezone || null, rules: x.hours.rules.map((q) => ruleBody(q as unknown as PieceBody)) },
        closed_destination: x.hours.mode === 'always' ? null : x.closed_destination,
        cid_name_prefix: x.cid_name_prefix, is_enabled: x.is_enabled,
      };
    }
    case 'ivr': {
      const x = r as IvrMenu;
      return {
        name: x.name, extension: x.extension, greeting_media_id: x.greeting_media_id, invalid_media_id: x.invalid_media_id ?? null,
        timeout_sec: x.timeout_sec, digit_timeout_sec: x.digit_timeout_sec ?? 3, max_attempts: x.max_attempts,
        answer_after_rings: x.answer_after_rings ?? 0, direct_dial: x.direct_dial,
        timeout_destination: x.timeout_destination, invalid_destination: x.invalid_destination,
        options: x.options.map((o) => ({ digit: o.digit, ...(o.label ? { label: o.label } : {}), destination: o.destination })),
      };
    }
    case 'ring_group': {
      const x = r as RingGroup;
      const steps = x.steps.length > 0 ? x.steps : [{ seq: 1, strategy: x.strategy, ring_sec: x.ring_time_sec, prompt_media_id: null }];
      return {
        name: x.name, extension: x.extension, strategy: x.strategy, ring_time_sec: x.ring_time_sec, skip_busy: x.skip_busy,
        timeout_destination: x.timeout_destination, members: [],
        steps: steps.map((s) => ({
          strategy: ['simultaneous', 'sequential', 'random'].includes(s.strategy) ? s.strategy : 'sequential',
          ring_sec: s.ring_sec, prompt_media_id: s.prompt_media_id ?? null,
          members: x.members.filter((m) => ((m as { step?: number }).step ?? 1) === s.seq)
            .flatMap((m): PieceBody[] => (m.extension_id ? [{ extension_id: m.extension_id }] : m.external_number ? [{ external_number: m.external_number }] : [])),
        })),
        outside_extension_id: x.outside_extension_id ?? null,
      };
    }
    case 'queue': {
      const x = r as Queue;
      return {
        name: x.name, extension: x.extension, strategy: x.strategy, announce_position: x.announce_position,
        announce_interval_sec: x.announce_interval_sec, periodic_media_id: x.periodic_media_id ?? null,
        periodic_interval_sec: x.periodic_interval_sec ?? 60, exit_key: x.exit_key ?? null, exit_destination: x.exit_destination ?? null,
        max_wait_sec: x.max_wait_sec, max_waiting_calls: x.max_waiting_calls, wrapup_sec: x.wrapup_sec,
        require_disposition: x.require_disposition ?? false, workspace_controls: x.workspace_controls ?? {},
        sticky_agent_days: x.sticky_agent_days ?? null, sticky_ring_sec: x.sticky_ring_sec ?? 15,
        timeout_destination: x.timeout_destination, moh_media_id: x.moh_media_id ?? null,
        agents: x.agents.map((a) => ({ extension_id: a.extension_id, tier_level: a.tier_level, tier_position: a.tier_position })),
      };
    }
    case 'message': {
      const x = r as FlowMessage;
      return { name: x.name, media_id: x.media_id, next_destination: x.next_destination };
    }
    case 'hours': {
      const x = r as HoursCheck;
      return {
        name: x.name,
        hours: x.hours.mode === 'own'
          ? { mode: 'own', timezone: x.hours.timezone || null, rules: x.hours.rules.map((q) => ruleBody(q as unknown as PieceBody)) }
          : { mode: 'schedule', schedule_id: x.hours.schedule_id ?? '' },
        open_destination: x.open_destination, closed_destination: x.closed_destination,
      };
    }
  }
}

// ------------------------------------------------------------------ version history (docs/41 §12)
/**
 * Every routing change is a version: a Classic save, a publish, a restore. A route's history is the
 * versions that changed it or any step its calls pass through; each names those steps with their
 * settings before and after, and the portal words the difference.
 */
export const RoutingVersionSourceSchema = z.enum(['start', 'classic', 'flow', 'restore']);
export type RoutingVersionSource = z.infer<typeof RoutingVersionSourceSchema>;

export const VersionStepSchema = z.object({
  kind: FlowPieceKindSchema,
  id: IdSchema,
  name: z.string(),
  /** Its settings before this version; null when it is new in it. */
  before: z.record(z.unknown()).nullable(),
  /** Its settings after; null when this version deleted it. */
  after: z.record(z.unknown()).nullable(),
  /** The other routes that reach it. */
  shared_with: z.array(z.string()),
});
export type VersionStep = z.infer<typeof VersionStepSchema>;

export const RoutingVersionSchema = z.object({
  id: IdSchema,
  at: TimestampSchema,
  /** Who made it; null where history starts. */
  by: z.string().nullable(),
  source: RoutingVersionSourceSchema,
  /** The Classic page a save came from. */
  page: FlowPieceKindSchema.nullable(),
  /** For a restore, or a publish of a restored draft: the version it went back to. */
  restored_from: z.object({ id: IdSchema, at: TimestampSchema, name: z.string().nullable() }).nullable(),
  name: z.string().nullable(),
  steps: z.array(VersionStepSchema),
});
export type RoutingVersion = z.infer<typeof RoutingVersionSchema>;

/** What restoring a route to a version would change from now, and anything that stops it. */
export const RestorePlanSchema = z.object({
  mode: RoutingModeSchema,
  changes: z.array(VersionStepSchema),
  /** Things the version needs that no longer exist. Any at all and the restore is refused (owner, 7 October 2026). */
  missing: z.array(z.object({
    what: z.enum(['extension', 'mailbox', 'recording', 'schedule', 'ivr', 'ring_group', 'queue', 'message', 'hours']),
    name: z.string().nullable(),
    /** The step that needs it. */
    used_by: z.string(),
  })),
  /** Numbers the route had then and not now: never moved back (owner, 7 October 2026), only said. */
  numbers: z.array(z.object({ e164: z.string(), now_on: z.string().nullable() })),
  /** Flows mode: the unpublished draft a restore replaces. */
  draft: z.object({ changes: z.number().int(), updated_at: TimestampSchema }).nullable(),
});
export type RestorePlan = z.infer<typeof RestorePlanSchema>;

export const RouteVersionSchema = z.object({
  version: RoutingVersionSchema,
  /** Every piece the route reached just after it, with its settings then. */
  pieces: z.array(z.object({ kind: FlowPieceKindSchema, id: IdSchema, body: z.record(z.unknown()) })),
  plan: RestorePlanSchema,
});
export type RouteVersion = z.infer<typeof RouteVersionSchema>;

export const RestoreResultSchema = z.object({
  mode: RoutingModeSchema,
  /** Flows: the draft the restore opened as. */
  draft_id: IdSchema.nullable(),
  /** Classic: steps written. */
  applied: z.number().int(),
});
export type RestoreResult = z.infer<typeof RestoreResultSchema>;

export const VersionNameSaveSchema = z.object({ name: z.string().trim().max(80).nullable() }).strict();
